/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms 
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this 
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and 
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

import React from "react";
import { useTheme } from "../../theme/ThemeContext";

/**
 * ProgressBarDialog
 * @param {object} props
 *  - open (boolean): Show/hide dialog
 *  - text_message (string): Message to display
 *  - cancellable (boolean): Show Cancel button if true
 *  - onCancel (function): Callback for cancel
 */
export default function ProgressBarDialog({
    open,
    text_message = "Operation in progress...",
    cancellable = false,
    onCancel = null,
}) {
    const { theme } = useTheme();

    if (!open) return null;

    return (
        <div
            style={{
                position: "fixed",
                left: 0, top: 0, right: 0, bottom: 0,
                zIndex: 9999,
                background: "rgba(30,40,60,0.14)",
                backdropFilter: "blur(1.5px)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                pointerEvents: "auto",  // Block all app interactions
            }}
            aria-modal="true"
            role="dialog"
            tabIndex={-1}
        >
            <div
                style={{
                    minWidth: 300,
                    maxWidth: "90vw",
                    background: "#fff",
                    borderRadius: theme.cardRadius,
                    boxShadow: "0 6px 30px rgba(0,0,0,.11)",
                    padding: 32,
                    textAlign: "center",
                    fontFamily: theme.fontFamily,
                }}
            >
                {/* Message */}
                <div
                    style={{
                        fontWeight: 600,
                        fontSize: 18,
                        color: theme.primary,
                        marginBottom: 26,
                        letterSpacing: ".02em",
                    }}
                >
                    {text_message}
                </div>

                {/* Animated Progress Bar */}
                <div style={{
                    width: "100%",
                    height: 12,
                    borderRadius: theme.buttonRadius,
                    background: "#f3f3fa",
                    overflow: "hidden",
                    marginBottom: cancellable ? 28 : 0
                }}>
                    <div
                        className="progressbar-dialog-bar"
                        style={{
                            height: "100%",
                            width: "60%", // visually show partial progress
                            minWidth: "18%",
                            background: `linear-gradient(90deg, ${theme.primary}, ${theme.secondary})`,
                            borderRadius: theme.buttonRadius,
                            transition: "width 0.6s cubic-bezier(.4,0,.2,1)",
                            animation: "progressbar-animate 2s linear infinite"
                        }}
                    />
                </div>

                {/* Cancel Button */}
                {cancellable && (
                    <button
                        onClick={onCancel}
                        style={{
                            marginTop: 14,
                            background: theme.secondary,
                            color: "#fff",
                            border: "none",
                            fontWeight: 600,
                            borderRadius: theme.buttonRadius,
                            fontFamily: theme.fontFamily,
                            fontSize: 16,
                            padding: "7px 24px",
                            boxShadow: "0 2px 6px rgba(30,58,138,0.08)",
                            cursor: "pointer"
                        }}
                    >
                        Cancel
                    </button>
                )}
            </div>
            {/* Prevent interaction with underlying content */}
            <div style={{
                position: "fixed",
                left: 0, top: 0, right: 0, bottom: 0,
                pointerEvents: "auto"
            }} />
            <style>
                {`
        @keyframes progressbar-animate {
          0% { width: 10%; }
          50% { width: 90%; }
          100% { width: 10%; }
        }
        .progressbar-dialog-bar {
          animation: progressbar-animate 1.4s cubic-bezier(.4,0,.2,1) infinite;
        }
        `}
            </style>
        </div>
    );
}
